import React, { useContext } from 'react';
import style from './typeDisplay.scss';
import {
  extractType,
  isArrayType,
  isPromiseType,
} from '../../../../../TypeDetails/utils';
import { SIZES, WEIGHTS, Text } from '../../../../../PageElements/Text';
import { StoreContext } from '../../../../../../appContext/StoreProvider';
import { isLink } from '../../../../../TypeDetails/NestedTable/NestedTableNameDisplay/utils';
import { typeToLink } from '../../../../../../../utils/locationUtils';
import { CustomLink } from '../../../../../PageElements/CustomLink';
import {
  isLegacyType,
  LegacyComplexType,
  LegacySingleType,
  Type,
  TypeInServiceJson,
  TypeParameter,
} from '../../../../../TypeDetails/typeParser';
import { getTypeSuffix } from '../../../../../../../utils/typeUtils';
import { getTypeDisplayName } from '../../../../../TypeDetails/NestedTable/utils';

interface TypeDisplayTextProps {
  type: string;
  displayName?: string;
  forTypeTableColumn: boolean;
  JSLayoutType: boolean;
}

const TypeDisplayText = ({
  type,
  displayName,
  forTypeTableColumn = false,
  JSLayoutType = false,
}: TypeDisplayTextProps) => {
  const { store } = useContext(StoreContext);
  const shouldDisplayComplexType =
    store.viewer?.viewerConfig?.displayOptions?.showComplexTypesInTable;

  const isReferenceToApi = isLink(store.docsTree, type);
  const shouldRenderLink = shouldDisplayComplexType && isReferenceToApi;
  const typeToPresent = getTypeDisplayName({
    type,
    store,
    typeDisplayName: displayName,
    JSLayoutType,
  });

  return (
    <Text
      link={shouldRenderLink}
      weight={WEIGHTS.normal}
      inline
      tagName="span"
      dataHook="type-display-text"
      size={forTypeTableColumn ? SIZES.small : SIZES.medium}
    >
      {shouldRenderLink ? (
        <CustomLink to={typeToLink(type)}>{typeToPresent}</CustomLink>
      ) : (
        typeToPresent
      )}
    </Text>
  );
};

interface TypeDisplayProps {
  type:
    | Type
    | LegacySingleType
    | TypeInServiceJson
    | TypeParameter
    | TypeParameter[];
  forTypeParameters?: boolean;
  displayName?: string;
  forTypeTableColumn?: boolean;
  JSLayoutType?: boolean;
}

export const TypeDisplay = ({
  type,
  forTypeParameters = false,
  displayName = '',
  forTypeTableColumn = false,
  JSLayoutType,
}: TypeDisplayProps) => {
  const gotMultipleTypes = Array.isArray(type);

  if (gotMultipleTypes) {
    const typeAsArray = type as (LegacySingleType | Type | TypeParameter)[];
    return (
      <div className={style.multipleTypes} data-hook="multiple-types">
        {typeAsArray.map((currentType, i) => (
          <React.Fragment key={`single-type-display-${i}`}>
            <TypeDisplay
              type={currentType}
              // @ts-expect-error
              displayName={currentType.displayName}
              forTypeTableColumn={forTypeTableColumn}
              JSLayoutType={JSLayoutType}
            />
            {forTypeParameters
              ? i !== typeAsArray.length - 1 && <Text>,&nbsp;</Text>
              : i !== typeAsArray.length - 1 && <Text>&nbsp;|&nbsp;</Text>}
          </React.Fragment>
        ))}
      </div>
    );
  }

  if ((isLegacyType(type) && isArrayType(type)) || isPromiseType(type)) {
    /**
     * We enter this 'if' scope if the type is in the legacy schema of:
     * {name: 'array', typeParams: [...]}
     */
    const typeAsLegacyComplexType = type as LegacyComplexType;
    return (
      <Text
        tagName="span"
        weight={WEIGHTS.normal}
        inline
        dataHook="complex-type-display-text"
        size={forTypeTableColumn ? SIZES.small : SIZES.medium}
      >
        {isArrayType(typeAsLegacyComplexType) && `Array<`}
        {isPromiseType(typeAsLegacyComplexType) && `Promise<`}
        <TypeDisplay
          type={extractType(typeAsLegacyComplexType)}
          displayName={displayName}
          forTypeTableColumn={forTypeTableColumn}
          JSLayoutType={JSLayoutType}
        />
        {`>`}
      </Text>
    );
  }

  const typeAsNewTypeSchema = type as Type;

  if (typeAsNewTypeSchema.complexType) {
    const prefix =
      typeAsNewTypeSchema.complexType.nativeType ||
      getTypeSuffix(
        typeAsNewTypeSchema.complexType.displayName ||
          typeAsNewTypeSchema.complexType.referenceType,
      ); // if inside the complexType object there is a `displayName`, then we should display it in the UI (can happen after name collision)

    return (
      <Text
        tagName="span"
        weight={WEIGHTS.normal}
        inline
        dataHook="complex-type-display-text"
        size={forTypeTableColumn ? SIZES.small : SIZES.medium}
      >
        {`${prefix}<`}
        <TypeDisplay
          type={extractType(typeAsNewTypeSchema)}
          forTypeParameters={true}
          forTypeTableColumn={forTypeTableColumn}
          JSLayoutType={JSLayoutType}
        />
        {`>`}
      </Text>
    );
  }

  const typeAsTypeParameterSchema = type as TypeParameter;
  if (typeAsTypeParameterSchema.unionType) {
    return (
      <TypeDisplay
        type={typeAsTypeParameterSchema.unionType.type}
        forTypeTableColumn={forTypeTableColumn}
        JSLayoutType={JSLayoutType}
      />
    );
  }

  /* The code here is reached if one of the conditions here is true:
  1. The type is an object of a native or a reference type according to the proto schema, e.g { nativeType: 'string' }
  2. The type is a legacy type of a non-complex type (native type or reference type) - in the form of a string
   */
  const simpleType = type as
    | string
    | { nativeType: string }
    | { referenceType: string };
  const typeToDisplay = extractType(simpleType) as string;
  return (
    <TypeDisplayText
      type={typeToDisplay}
      displayName={displayName}
      forTypeTableColumn={forTypeTableColumn}
      JSLayoutType={JSLayoutType}
    />
  );
};
